import {skipToken} from '@reduxjs/toolkit/query';
import {Card, DataStatus} from 'platform/components';
import {Box, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  GetSupplierPriceListItemRequest,
  useGetSupplierPriceListItemQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {RequiredTestIdProps} from 'shared/web';

interface PriceListDetailAdditionalInformationProps extends RequiredTestIdProps {
  searchCriteria: GetSupplierPriceListItemRequest['body'];
}

export function PriceListDetailAdditionalInformation(
  props: PriceListDetailAdditionalInformationProps
) {
  const {data, isFetching, isError} = useGetSupplierPriceListItemQuery(
    isNotNilOrEmpty(props.searchCriteria.catalogNumber) ? {body: props.searchCriteria} : skipToken
  );

  const additionalInformation = isError ? null : data?.additionalInformation;

  const {content, color, size} = match(isNotNilOrEmpty(additionalInformation))
    .with(true, () => ({
      content: additionalInformation,
      color: 'primary' as const,
      size: 'xSmall' as const,
    }))
    .otherwise(() => ({
      content: i18n.t('entity.warehouse.labels.noAdditionalInformationAvailable'),
      color: 'tertiary' as const,
      size: 'base' as const,
    }));

  return (
    <Card
      variant="inlineGrey"
      title={i18n.t('general.labels.additionalInformation')}
      flags={[
        {
          isSubtle: true,
          label: i18n.t('entity.warehouse.labels.sourcedAutomatically'),
          colorScheme: 'blue',
          size: 'small',
        },
      ]}
    >
      <VStack spacing={1}>
        <DataStatus isLoading={isFetching} spinnerSize="small">
          <Box paddingVertical={additionalInformation ? 0 : 6}>
            <Text
              preWrap
              color={color}
              size={size}
              align={additionalInformation ? 'left' : 'center'}
            >
              {content}
            </Text>
          </Box>
        </DataStatus>
      </VStack>
    </Card>
  );
}
